import { createSelector } from '@reduxjs/toolkit'
import { CONTRACT_LIFECYCLES } from '../../../../pages/Dispatch/lib/lifecycles'
import type { DashboardState } from './dashboardSlice'
import type { NormalizedOrder } from '../../../../pages/Dispatch/lib/normalizeOrder'
import { FILTER_FIELDS, type FilterCtx } from '../../../../pages/Dispatch/lib/filterFields'
import type { DistributionZone } from '../../../../pages/Dispatch/optimizer/lib/types'
import { vehicleTypeDefaults } from '../../../../pages/Dispatch/optimizer/lib/vehicleTypes'

/**
 * Minimal shape of the slices of the global store this dashboard reads.
 * The full TMS_PORTAL store is `any`-typed; we narrow only what we use.
 */
interface RootSlice {
  data: { orderstatusesref: any[] | null; vehicletyperef?: any[] | null }
  dispatchData: { drivers: any[] | null; trucksList: any[] | null }
  user: { regions: any[] | null }
  dashboard: DashboardState
}

export interface Lifecycle {
  name: string
  /**
   * Opaque internal sort key. Its meaning depends on `columnMode`:
   *   - lifecycle mode: the min Firestore `sortOrder` of the substatuses in this lifecycle.
   *   - driver mode: the position index within the saved driver-column order.
   * Both produce a stable column order when sorted ascending. Consumers should
   * iterate the array in its returned order rather than reading this value.
   */
  sortKey: number
}

export interface SubstatusMaps {
  substatusMap: Record<string, string>
  substatusOrderMap: Record<string, number>
  statusColorMap: Record<string, string>
  lifecycles: Lifecycle[]
}

// Stable empty-array sentinel so the null/undefined fallback returns a constant
// reference, preserving createSelector memoization during the data-loading window.
const EMPTY_ARRAY: any[] = []

const selectOrderStatusesRef = (s: RootSlice) => s.data?.orderstatusesref ?? EMPTY_ARRAY
const selectDispatchDrivers = (s: RootSlice) => s.dispatchData?.drivers ?? EMPTY_ARRAY
const selectTrucksList = (s: RootSlice) => s.dispatchData?.trucksList ?? EMPTY_ARRAY
const selectUserRegions = (s: RootSlice) => s.user?.regions ?? EMPTY_ARRAY
export const selectDashboard = (s: RootSlice) => s.dashboard

/**
 * Derive the four substatus maps from TMS_PORTAL's already-loaded
 * `data.orderstatusesref`. Replaces the Vue dashboard's bespoke
 * GLOBALDEF_SUBSTATUSES fetch. Only `isOrderStatus` substatuses contribute.
 */
export const selectSubstatusMaps = createSelector(
  [selectOrderStatusesRef],
  (orderStatusesRef): SubstatusMaps => {
    const substatusMap: Record<string, string> = {}
    const substatusOrderMap: Record<string, number> = {}
    const statusColorMap: Record<string, string> = {}
    const lifecycleOrders: Record<string, number> = {}

    orderStatusesRef.forEach((entry: any) => {
      const d = entry?.value ?? {}
      if (!d.isOrderStatus || !d.value || !d.lifeCycle) return
      substatusMap[d.value] = d.lifeCycle
      statusColorMap[d.value] = d.color || ''
      const order = typeof d.sortOrder === 'number' ? d.sortOrder : Number.MAX_SAFE_INTEGER
      substatusOrderMap[d.value] = order
      if (lifecycleOrders[d.lifeCycle] === undefined || order < lifecycleOrders[d.lifeCycle]) {
        lifecycleOrders[d.lifeCycle] = order
      }
    })

    // Canonical contract order first, then any non-contract lifecycles by their sortOrder.
    const contractIndex = new Map<string, number>(
      CONTRACT_LIFECYCLES.map((n, i) => [n, i]),
    )
    const lifecycles: Lifecycle[] = Object.entries(lifecycleOrders)
      .map(([name, sortKey]) => ({ name, sortKey }))
      .sort((a, b) => {
        const ai = contractIndex.has(a.name) ? (contractIndex.get(a.name) as number) : Number.MAX_SAFE_INTEGER
        const bi = contractIndex.has(b.name) ? (contractIndex.get(b.name) as number) : Number.MAX_SAFE_INTEGER
        if (ai !== bi) return ai - bi
        return a.sortKey - b.sortKey || a.name.localeCompare(b.name)
      })

    return { substatusMap, substatusOrderMap, statusColorMap, lifecycles }
  },
)

export interface DriverOption {
  id: string
  firestoreDocId: string
  driverCode: string
  label: string
}

/** Driver options sourced from TMS_PORTAL's dispatchData (replaces root availableDrivers). */
export const selectDrivers = createSelector([selectDispatchDrivers], (drivers): DriverOption[] =>
  drivers.map((d: any) => ({
    id: d?.value?.id ?? '',
    firestoreDocId: d?.value?.firestoreDocId ?? '',
    driverCode: d?.value?.driverCode ?? d?.value?.id ?? '',
    label: d?.label ?? '',
  })),
)

// ─── Region-filtered driver options for Today (dashboard) mode ────────────────
//
// Reads `dashboard.region` and resolves zone→region via `optimizer.distributionZones`
// (that slice loads the global zone map when the optimizer page is visited).
// If the zone map is empty (fresh session, optimizer not yet visited) we fall
// back to returning all drivers so the modal is never empty.

const EMPTY_OPT_ZONES: DistributionZone[] = []
const selectOptimizerZones = (s: any): DistributionZone[] =>
  (s as any).optimizer?.distributionZones ?? EMPTY_OPT_ZONES
const selectDashboardRegionId = (s: any): string => (s as any).dashboard?.region ?? ''

const selectZoneIdsForRegion = createSelector(
  [selectOptimizerZones, selectDashboardRegionId],
  (zones, region): Set<string> => {
    if (!region) return new Set<string>()
    const result = new Set<string>()
    zones.forEach((z) => { if (z.region === region) result.add(z.id) })
    return result
  },
)

/**
 * Driver options filtered to the currently selected dashboard region, in the
 * same `DriverOption` shape as `selectDrivers`.
 *
 * A driver is included when:
 *   - `isAllDistributionZones === true`, OR
 *   - at least one of its zones belongs to the selected region.
 *
 * Falls back to all drivers when the region is unset or the zone map is empty.
 */
export const selectRegionDriversDashboard = createSelector(
  [selectDispatchDrivers, selectZoneIdsForRegion, selectDashboardRegionId, selectOptimizerZones],
  (drivers, zoneIds, region, zones): DriverOption[] => {
    const zonesLoaded = zones.length > 0
    const filtered = (region && zonesLoaded)
      ? drivers.filter((d: any) => {
          if (d?.value?.isAllDistributionZones === true) return true
          const dZones: string[] = d?.value?.zones ?? []
          return dZones.some((zId) => zoneIds.has(zId))
        })
      : drivers

    return filtered.map((d: any) => ({
      id: d?.value?.id ?? '',
      firestoreDocId: d?.value?.firestoreDocId ?? '',
      driverCode: d?.value?.driverCode ?? d?.value?.id ?? '',
      label: d?.label ?? '',
    }))
  },
)

export interface TruckOption {
  id: string
  name: string
  region: string
  vehicleType: string
  weightCapacity: number
}

/** Vehicle options sourced from TMS_PORTAL's dispatchData.trucksList. */
export const selectTrucks = createSelector([selectTrucksList], (trucks): TruckOption[] =>
  trucks.map((t: any) => ({
    id: t?.value?.id ?? '',
    name: t?.label ?? '',
    region: t?.value?.region ?? '',
    vehicleType: t?.value?.vehicleType ?? '',
    weightCapacity: Number(t?.value?.weightCapacity) || 0,
  })),
)

/** Map of vehicle NAME → vehicleType, for labelling vehicle columns/cards. */
export const selectVehicleTypeByName = createSelector([selectTrucks], (trucks): Record<string, string> => {
  const m: Record<string, string> = {}
  trucks.forEach((t) => { if (t.name) m[t.name] = t.vehicleType || '' })
  return m
})

const selectVehicleTypeRef = (s: RootSlice) => s.data?.vehicletyperef ?? EMPTY_ARRAY

/**
 * Map of vehicle NAME → effective metric weight capacity (kg), mirroring the
 * optimizer: the per-vehicle FLEET `WeightCapacity` override when set, else the
 * vehicle-type default from `data.vehicletyperef`. 0 means no capacity known.
 */
export const selectWeightCapacityByVehicleName = createSelector(
  [selectTrucks, selectVehicleTypeRef],
  (trucks, vehicleTypeRef): Record<string, number> => {
    const m: Record<string, number> = {}
    trucks.forEach((t) => {
      if (!t.name) return
      const typeDefault = vehicleTypeDefaults(vehicleTypeRef, t.vehicleType)?.weightCapacity || 0
      m[t.name] = t.weightCapacity > 0 ? t.weightCapacity : typeDefault
    })
    return m
  },
)

/** Every board order (today's orders) keyed by id, for linked-pickup lookups. */
export const selectBoardOrdersById = createSelector([selectDashboard], (dash): Map<string, NormalizedOrder> =>
  new Map(dash.orders.map((o) => [o.id, o])),
)

export interface RegionOption {
  id: string
  name: string
}

/** Region options sourced from TMS_PORTAL's user.regions. */
export const selectRegions = createSelector([selectUserRegions], (regions): RegionOption[] =>
  regions.map((r: any) => ({
    id: r?.value?.id ?? '',
    name: r?.value?.name ?? r?.label ?? '',
  })),
)

/** Visible lifecycle columns: contract-ordered, honouring columnVisibility + lifecycleOrder. */
export const selectVisibleLifecycles = createSelector(
  [selectSubstatusMaps, selectDashboard],
  (maps, dash): Lifecycle[] => {
    const visible = maps.lifecycles.filter((l) => dash.columnVisibility[l.name] !== false).slice()
    if (dash.lifecycleOrder && dash.lifecycleOrder.length) {
      const idx = new Map(dash.lifecycleOrder.map((n, i) => [n, i]))
      return visible.sort((a, b) => {
        const ia = idx.has(a.name) ? (idx.get(a.name) as number) : Number.MAX_SAFE_INTEGER
        const ib = idx.has(b.name) ? (idx.get(b.name) as number) : Number.MAX_SAFE_INTEGER
        if (ia !== ib) return ia - ib
        return a.sortKey - b.sortKey
      })
    }
    return visible.sort((a, b) => a.sortKey - b.sortKey)
  },
)

/** Driver columns: every driver in the selected region (even with no orders),
 *  plus any driver seen on the current orders, plus an "Unassigned" bucket.
 *  Orders store Driver as "Name***email" → normalizeOrder keeps the name, so the
 *  column key is the driver NAME; region drivers' label "Name***username" maps to
 *  the same name. */
export const selectDriverColumnsAll = createSelector(
  [selectDashboard, selectRegionDriversDashboard],
  (dash, regionDrivers): Lifecycle[] => {
  const seen = new Set<string>()
  dash.orders.forEach((o) => {
    seen.add(o && o.Driver ? String(o.Driver) : 'Unassigned')
  })
  seen.add('Unassigned')
  // Every region driver gets a column, even with no orders assigned.
  regionDrivers.forEach((d) => {
    const name = (d.label || '').split('***')[0]
    if (name) seen.add(name)
  })

  const order = dash.driverColumnOrder || []
  const known = new Set(order)
  const ordered = order.filter((n) => seen.has(n))
  const remaining = Array.from(seen)
    .filter((n) => !known.has(n))
    .sort((a, b) => {
      if (a === 'Unassigned') return -1
      if (b === 'Unassigned') return 1
      return a.localeCompare(b)
    })
  return [...ordered, ...remaining].map((name, i) => ({ name, sortKey: i }))
})

/**
 * Vehicles (trucks) in the selected dashboard region. FLEET.Region stores the
 * region ID (the REGIONS doc id), which is exactly what `dashboard.region` holds —
 * so we match on the raw region ID, NOT the resolved region name (orders store the
 * name, but trucks do not). Falls back to all trucks only when the region is unset
 * (fresh session), so the vehicle columns are never spuriously empty.
 */
export const selectRegionVehiclesDashboard = createSelector(
  [selectTrucks, selectDashboardRegionId],
  (trucks, regionId): TruckOption[] =>
    regionId ? trucks.filter((t) => t.region === regionId) : trucks,
)

/** Vehicle columns: every vehicle in the selected region (even with no orders),
 *  plus any vehicle seen on the current orders, plus an "Unassigned" bucket.
 *  Column key is the vehicle NAME — orders store fields.Vehicle as the name, and a
 *  region vehicle's label is the same name. Mirrors selectDriverColumnsAll. */
export const selectVehicleColumnsAll = createSelector(
  [selectDashboard, selectRegionVehiclesDashboard],
  (dash, regionVehicles): Lifecycle[] => {
  const seen = new Set<string>()
  dash.orders.forEach((o) => {
    seen.add(o && o.Vehicle ? String(o.Vehicle) : 'Unassigned')
  })
  seen.add('Unassigned')
  // Every region vehicle gets a column, even with no orders assigned.
  regionVehicles.forEach((v) => {
    if (v.name) seen.add(v.name)
  })

  const order = dash.vehicleColumnOrder || []
  const known = new Set(order)
  const ordered = order.filter((n) => seen.has(n))
  const remaining = Array.from(seen)
    .filter((n) => !known.has(n))
    .sort((a, b) => {
      if (a === 'Unassigned') return -1
      if (b === 'Unassigned') return 1
      return a.localeCompare(b)
    })
  return [...ordered, ...remaining].map((name, i) => ({ name, sortKey: i }))
})

/** The columns to render — lifecycle, driver, or vehicle columns, per columnMode. */
export const selectVisibleColumns = createSelector(
  [selectDashboard, selectVisibleLifecycles, selectDriverColumnsAll, selectVehicleColumnsAll],
  (dash, lifecycleCols, driverCols, vehicleCols): Lifecycle[] => {
    if (dash.columnMode === 'driver') {
      return driverCols.filter((c) => dash.driverColumnVisibility[c.name] !== false)
    }
    if (dash.columnMode === 'vehicle') {
      return vehicleCols.filter((c) => dash.vehicleColumnVisibility[c.name] !== false)
    }
    return lifecycleCols
  },
)

export const selectBulkSelectionSet = createSelector(
  [selectDashboard],
  (dash): Set<string> => new Set(dash.bulkSelection || []),
)

// Selector for the linkedGroups slice (kept in root store, not dashboard slice)
const selectLinkedGroupsByRef = (s: any): Record<string, any> =>
  (s as any).linkedGroups?.byRef ?? {}

/**
 * Selected order plus its linked group members — for linked-group highlighting.
 *
 * Resolution priority:
 *  1. New-schema: `order.linkedRef` (a TMS_LINK_REFS ref id) → look up all stop
 *     orderId values from `state.linkedGroups.byRef[refId]`.
 *  2. Old-schema fallback: `order.linkedOrder` (direct doc id pair) — kept for
 *     orders that have not yet been migrated to string refs.
 */
export const selectSelectedOrderIdSet = createSelector(
  [selectDashboard, selectLinkedGroupsByRef],
  (dash, byRef): Set<string> => {
    const set = new Set<string>()
    const id = dash.selectedOrderId
    if (!id) return set
    set.add(id)
    const order = dash.orders.find((o) => o.id === id)

    // New-schema: resolve all group members via linkedRef → byRef
    const linkedRef = order?.linkedRef
    if (linkedRef && byRef[linkedRef]) {
      const group = byRef[linkedRef]
      const stops: Array<{ orderId: string }> = group?.stops ?? []
      stops.forEach((s) => {
        if (s.orderId) set.add(s.orderId)
      })
    } else {
      // Old-schema fallback: direct paired linkedOrder field
      if (order?.linkedOrder) set.add(order.linkedOrder)
      dash.orders.forEach((o) => {
        if (o.linkedOrder && o.linkedOrder === id) set.add(o.id)
      })
    }
    return set
  },
)

/** The order list after applying the toolbar filters — drives the kanban + map.
 *  Iterates the FILTER_FIELDS registry so new filter fields apply automatically.
 *  (matches() only needs substatusMap; option lists aren't needed for filtering.) */
export const selectFilteredOrders = createSelector(
  [selectDashboard, selectSubstatusMaps],
  (dash, maps): NormalizedOrder[] => {
    const filters = dash.filters as Record<string, any>
    const term = (filters.search || '').trim().toLowerCase()
    const ctx: FilterCtx = {
      orders: dash.orders,
      trucks: [],
      drivers: [],
      lifecycles: [],
      substatusMap: maps.substatusMap,
    }
    const active = FILTER_FIELDS
      .map((f) => ({ f, selected: (filters[f.key] as string[]) || [] }))
      .filter((x) => x.selected.length > 0)

    return dash.orders.filter((o) => {
      for (const { f, selected } of active) {
        if (!f.matches(o, selected, ctx)) return false
      }
      if (term) {
        const haystack = `${o.invoice || ''} ${o.address || ''} ${o.client || ''}`.toLowerCase()
        if (!haystack.includes(term)) return false
      }
      return true
    })
  },
)
